import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { X, ChevronLeft, ChevronRight, Images, Loader2, ArrowLeft, Filter, ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category options
const CATEGORIES = [
  'All Categories',
  'Infrastructure',
  'Pharma',
  'Agriculture',
  'Mining',
  'Logistics',
  'Energy',
  'Manufacturing',
  'Technology',
  'ClimateTech',
  'Trade',
  'Environment',
  'Solar',
  'Water'
];

// Photo Slider Component
const PhotoSlider = ({ photos, onPhotoClick }) => {
  const sliderRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (sliderRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener('scroll', checkScroll);
      return () => slider.removeEventListener('scroll', checkScroll);
    }
  }, [photos]);

  const scroll = (direction) => {
    if (sliderRef.current) {
      const scrollAmount = 320;
      sliderRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (!photos || photos.length === 0) return null;

  return (
    <div className="relative group">
      {/* Left Arrow */}
      {canScrollLeft && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[#0A0A0A]/90 border border-white/20 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#D4AF37] hover:text-[#0A0A0A]"
        >
          <ChevronLeft size={20} />
        </button>
      )}

      {/* Photos */}
      <div
        ref={sliderRef}
        className="flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth pb-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {photos.map((photo, index) => (
          <motion.div
            key={photo.id}
            whileHover={{ scale: 1.03 }}
            className="flex-shrink-0 w-[200px] h-[150px] rounded-xl overflow-hidden cursor-pointer"
            onClick={() => onPhotoClick(index)}
          >
            <img
              src={`${API_URL}${photo.url}`}
              alt={photo.caption || `Photo ${index + 1}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </motion.div>
        ))}
      </div>

      {/* Right Arrow */}
      {canScrollRight && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[#0A0A0A]/90 border border-white/20 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#D4AF37] hover:text-[#0A0A0A]"
        >
          <ChevronRight size={20} />
        </button>
      )}
    </div>
  );
};

// Individual Topic Page Component
const TopicPage = ({ topic, onBack }) => {
  const [photos, setPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    fetchPhotos();
  }, [topic.id]);

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gallery/photos/${topic.id}`);
      setPhotos(response.data);
    } catch (error) {
      console.error('Error fetching photos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') setLightboxOpen(false);
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, photos]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button & Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-white/60 hover:text-[#D4AF37] transition-colors mb-6"
          >
            <ArrowLeft size={20} />
            Back to Gallery
          </button>
          
          <h1 className="text-3xl sm:text-4xl font-bold text-white font-['Playfair_Display'] mb-2">
            {topic.name}
          </h1>
          {topic.description && (
            <p className="text-white/60 max-w-2xl">{topic.description}</p>
          )}
          <p className="text-sm text-[#D4AF37] mt-2">{photos.length} photos</p>
        </motion.div>

        {/* Photos Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={40} className="animate-spin text-[#D4AF37]" />
          </div>
        ) : photos.length === 0 ? (
          <div className="text-center py-20">
            <Images size={48} className="mx-auto text-white/20 mb-4" />
            <p className="text-white/50">No photos in this album yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {photos.map((photo, index) => (
              <motion.div
                key={photo.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ scale: 1.02 }}
                className="aspect-square rounded-xl overflow-hidden cursor-pointer group"
                onClick={() => openLightbox(index)}
              >
                <img
                  src={`${API_URL}${photo.url}`}
                  alt={photo.caption || `Photo ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  loading="lazy"
                />
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && photos[currentImageIndex] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={() => setLightboxOpen(false)}
          >
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-10"
            >
              <X size={24} />
            </button>

            {photos.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  className="absolute left-4 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                >
                  <ChevronLeft size={28} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="absolute right-4 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                >
                  <ChevronRight size={28} />
                </button>
              </>
            )}

            <motion.img
              key={currentImageIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              src={`${API_URL}${photos[currentImageIndex]?.url}`}
              alt=""
              className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
              {currentImageIndex + 1} / {photos.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Main Gallery Page Component
export const GalleryPage = () => {
  const [topics, setTopics] = useState([]);
  const [filteredTopics, setFilteredTopics] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [lightboxTopic, setLightboxTopic] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchGallery();
  }, []);

  useEffect(() => {
    filterTopics();
  }, [topics, selectedCategory, searchQuery]);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gallery/all`);
      setTopics(response.data);
      setFilteredTopics(response.data);
    } catch (error) {
      console.error('Error fetching gallery:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterTopics = () => {
    let filtered = [...topics];
    
    // Filter by category
    if (selectedCategory !== 'All Categories') {
      filtered = filtered.filter(topic => topic.category === selectedCategory);
    }
    
    // Filter by search query (title)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(topic => 
        topic.name.toLowerCase().includes(query) ||
        (topic.description && topic.description.toLowerCase().includes(query))
      );
    }
    
    setFilteredTopics(filtered);
  };

  const openLightbox = (topic, index) => {
    setLightboxTopic(topic);
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxTopic(null);
  };

  const nextImage = () => {
    if (lightboxTopic) {
      setLightboxIndex((prev) => 
        prev < lightboxTopic.photos.length - 1 ? prev + 1 : 0
      );
    }
  };

  const prevImage = () => {
    if (lightboxTopic) {
      setLightboxIndex((prev) => 
        prev > 0 ? prev - 1 : lightboxTopic.photos.length - 1
      );
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxTopic) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxTopic]);

  // Show individual topic page
  if (selectedTopic) {
    return <TopicPage topic={selectedTopic} onBack={() => setSelectedTopic(null)} />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 size={40} className="animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <p className="text-xs tracking-[0.3em] text-[#D4AF37] mb-4">MOMENTS & MEMORIES</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white font-['Playfair_Display'] mb-4">
            Photo <span className="text-[#D4AF37]">Gallery</span>
          </h1>
          <p className="text-white/60 max-w-2xl mx-auto">
            A glimpse into our journey across Africa — meetings, partnerships, and milestones that define our story.
          </p>
        </motion.div>

        {/* Filter Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-col sm:flex-row gap-4 items-center justify-center mb-10"
        >
          {/* Category Dropdown */}
          <div className="relative w-full sm:w-64">
            <Filter size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full pl-10 pr-10 py-3 bg-[#111] border border-white/20 rounded-xl text-white focus:outline-none focus:border-[#D4AF37] appearance-none cursor-pointer"
            >
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat} className="bg-[#1a1a1a]">{cat}</option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
          </div>

          {/* Search Input */}
          <div className="relative w-full sm:w-72">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by title..."
              className="w-full px-4 py-3 bg-[#111] border border-white/20 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]"
            />
          </div>
        </motion.div>

        {/* Results count */}
        {(selectedCategory !== 'All Categories' || searchQuery) && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-white/50 text-sm mb-6"
          >
            Showing {filteredTopics.length} {filteredTopics.length === 1 ? 'album' : 'albums'}
            {selectedCategory !== 'All Categories' && ` in ${selectedCategory}`}
            {searchQuery && ` matching "${searchQuery}"`}
          </motion.p>
        )}

        {filteredTopics.length === 0 ? (
          <div className="text-center py-20">
            <Images size={48} className="mx-auto text-white/20 mb-4" />
            <p className="text-white/50">
              {topics.length === 0 
                ? 'No photos yet. Check back soon!'
                : 'No albums found matching your filters.'
              }
            </p>
            {(selectedCategory !== 'All Categories' || searchQuery) && (
              <button
                onClick={() => { setSelectedCategory('All Categories'); setSearchQuery(''); }}
                className="mt-4 px-4 py-2 text-[#D4AF37] border border-[#D4AF37]/30 rounded-lg hover:bg-[#D4AF37]/10 transition-colors"
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-12">
            {filteredTopics.map((topic, topicIndex) => (
              <motion.div
                key={topic.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: topicIndex * 0.1 }}
                className="bg-[#111]/50 rounded-2xl p-6 border border-white/5"
              >
                {/* Topic Header */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h2 className="text-xl sm:text-2xl font-bold text-white font-['Playfair_Display']">
                        {topic.name}
                      </h2>
                      {topic.category && (
                        <span className="px-2 py-0.5 text-xs bg-[#D4AF37]/20 text-[#D4AF37] rounded-full">
                          {topic.category}
                        </span>
                      )}
                    </div>
                    {topic.description && (
                      <p className="text-white/50 text-sm mt-1">{topic.description}</p>
                    )}
                  </div>
                  <button
                    onClick={() => setSelectedTopic(topic)}
                    className="px-4 py-2 text-sm text-[#D4AF37] border border-[#D4AF37]/30 rounded-lg hover:bg-[#D4AF37]/10 transition-colors flex items-center gap-2"
                  >
                    View All
                    <span className="text-white/40">{topic.photo_count}</span>
                  </button>
                </div>

                {/* Photo Slider */}
                <PhotoSlider 
                  photos={topic.photos} 
                  onPhotoClick={(index) => openLightbox(topic, index)}
                />
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxTopic && lightboxTopic.photos[lightboxIndex] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
          >
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-10"
            >
              <X size={24} />
            </button>

            {lightboxTopic.photos.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  className="absolute left-4 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                >
                  <ChevronLeft size={28} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="absolute right-4 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                >
                  <ChevronRight size={28} />
                </button>
              </>
            )}

            <motion.img
              key={lightboxIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              src={`${API_URL}${lightboxTopic.photos[lightboxIndex]?.url}`}
              alt=""
              className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
              <span className="text-[#D4AF37]">{lightboxTopic.name}</span>
              <span className="mx-2">•</span>
              {lightboxIndex + 1} / {lightboxTopic.photos.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GalleryPage;
